import React, {useMemo, useState} from 'react';
import {GripVertical, X} from 'lucide-react';

/**
 * Generic multi-select chip picker for building an ordered list from a
 * catalog. Used by the BioMatch builder (recommended assays/products)
 * and the Product Builder (included assays).
 *
 * Add via the dropdown; remove via the chip's X. Order is preserved
 * by `sort_order` on the parent state — drag-handle hint is visual
 * only (drag-to-reorder is a future polish if asked).
 */

export interface PickerOption {
    /** Wire-level key the modal saves (assay_code or product id). */
    key: string;
    /** Human-readable label shown in chips and the dropdown. */
    label: string;
    /** Optional secondary line (e.g. price, code). */
    sublabel?: string;
}

interface Props {
    title: string;
    description?: string;
    /** Order matters — first = sort_order 0. */
    selectedKeys: string[];
    available: PickerOption[];
    onChange: (nextKeys: string[]) => void;
    /** Override placeholder for the empty dropdown state. */
    placeholder?: string;
    emptyText?: string;
}

const RecommendedListPicker: React.FC<Props> = ({
    title,
    description,
    selectedKeys,
    available,
    onChange,
    placeholder,
    emptyText,
}) => {
    const [draftKey, setDraftKey] = useState<string>('');

    const optionByKey = useMemo(() => {
        const map = new Map<string, PickerOption>();
        for (const o of available) map.set(o.key, o);
        return map;
    }, [available]);

    // Available options minus already-selected ones.
    const remaining = useMemo(() => available.filter((o) => !selectedKeys.includes(o.key)), [available, selectedKeys]);

    const add = (key: string) => {
        if (!key) return;
        if (selectedKeys.includes(key)) return;
        onChange([...selectedKeys, key]);
        setDraftKey('');
    };

    const remove = (key: string) => {
        onChange(selectedKeys.filter((k) => k !== key));
    };

    return (
        <div className="space-y-2">
            <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-700">{title}</h4>
                {description && <p className="text-[11px] text-gray-500">{description}</p>}
            </div>

            {selectedKeys.length === 0 ? (
                <p className="text-xs italic text-gray-400">{emptyText ?? 'Nothing selected yet.'}</p>
            ) : (
                <ul className="flex flex-wrap gap-1.5">
                    {selectedKeys.map((key) => {
                        const opt = optionByKey.get(key);
                        return (
                            <li
                                key={key}
                                className="inline-flex items-center gap-1.5 rounded-full border border-mld-primary/30 bg-mld-primary/10 px-2.5 py-1 text-xs text-mld-primary">
                                <GripVertical className="h-3 w-3 opacity-40" />
                                <span className="font-medium">{opt?.label ?? key}</span>
                                {opt?.sublabel && (
                                    <span className="text-[10px] text-mld-primary/70">{opt.sublabel}</span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => remove(key)}
                                    className="rounded-full p-0.5 hover:bg-mld-primary/20"
                                    aria-label={`Remove ${opt?.label ?? key}`}>
                                    <X className="h-3 w-3" />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            <div className="flex items-center gap-2">
                <select
                    value={draftKey}
                    onChange={(e) => add(e.target.value)}
                    disabled={remaining.length === 0}
                    className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-mld-primary focus:outline-none focus:ring-1 focus:ring-mld-primary disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400">
                    <option value="">
                        {available.length === 0
                            ? 'No options available'
                            : remaining.length === 0
                              ? 'All options selected'
                              : (placeholder ?? 'Add an option...')}
                    </option>
                    {remaining.map((o) => (
                        <option key={o.key} value={o.key}>
                            {o.label}
                            {o.sublabel ? ` — ${o.sublabel}` : ''}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

export default RecommendedListPicker;
